import { consoleLoginRealm, type ConsoleDomain } from "@/console-domain";

export interface StoredConsoleProfile {
  permissions: string[];
  domainScope: string;
}

interface RouteAccessRule {
  domains?: readonly ConsoleDomain[];
  all?: readonly string[];
  any?: readonly string[];
}

const routeAccessRules: Readonly<Record<string, RouteAccessRule>> = {
  "/": {},
  "/operations": { domains: ["root", "admin", "creator"] },
  "/chat-moderation": { all: ["moderation.review"] },
  "/users": { all: ["user.manage"] },
  "/notifications": { all: ["notification.manage"] },
  "/admin-accounts": { all: ["rbac.role.write"] },
  "/matches": { all: ["sports.match.write"] },
  "/data-center": { all: ["user.manage", "sports.match.write"] },
  "/system-settings": {
    any: ["app.config.write", "platform.payment.write", "audit.read"],
  },
  "/payment-settings": { all: ["platform.payment.write"] },
  "/app-settings": { all: ["app.config.write"] },
  "/kyc": { all: ["kyc.review"] },
  "/withdrawals": { all: ["finance.withdraw.read"] },
  "/approvals": { domains: ["root", "finance"] },
  "/audit": { all: ["audit.read"] },
  "/release-readiness": { all: ["release.read"] },
};

function normalizedPath(path: string) {
  if (path === "/") return path;
  return path.replace(/\/+$/, "") || "/";
}

export function parseStoredConsoleProfile(
  raw: string | null,
): StoredConsoleProfile | undefined {
  if (!raw) return undefined;
  try {
    const value = JSON.parse(raw) as Partial<StoredConsoleProfile>;
    if (
      typeof value.domainScope !== "string" ||
      !Array.isArray(value.permissions) ||
      value.permissions.some((permission) => typeof permission !== "string")
    ) {
      return undefined;
    }
    return {
      domainScope: value.domainScope.trim().toLowerCase(),
      permissions: value.permissions,
    };
  } catch {
    return undefined;
  }
}

export function profileMatchesConsoleDomain(
  profile: StoredConsoleProfile,
  domain: ConsoleDomain,
) {
  return profile.domainScope === consoleLoginRealm(domain).toLowerCase();
}

export function canAccessConsoleRoute(
  path: string,
  domain: ConsoleDomain,
  permissions: Iterable<string>,
) {
  if (normalizedPath(path) === "/login") return true;
  const rule = routeAccessRules[normalizedPath(path)];
  if (!rule) return false;
  if (rule.domains && !rule.domains.includes(domain)) return false;

  const granted = new Set(permissions);
  if (granted.has("*")) return true;
  if (rule.all && !rule.all.every((permission) => granted.has(permission))) {
    return false;
  }
  if (rule.any && !rule.any.some((permission) => granted.has(permission))) {
    return false;
  }
  return true;
}
